<template>
  <BaseModalSmall
    testId="sp-remove-wallet-modal"
    @close="$emit('close')"
  >
    <Flex
      direction="column"
      :gap="0.5"
    >
      <BaseText size="l">
        {{ $t('register.salonized_pay.dashboard.wallets.remove_modal.message', {
            name: balanceAccount.label,
          }) }}
      </BaseText>
      <BaseText color="secondary">
        {{ $t('register.salonized_pay.dashboard.wallets.remove_modal.description') }}
      </BaseText>
      <BaseText
        v-if="errorMessage"
        color="error"
        v-test="'sp-remove-wallet-error'"
      >
        {{ errorMessage }}
      </BaseText>
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        :disabled="loading"
        @click="$emit('close')"
        v-test="'sp-remove-wallet-cancel'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        color="error"
        :loading="loading"
        @click="submit"
        v-test="'sp-remove-wallet-submit'"
      >
        {{ $t('register.salonized_pay.dashboard.wallets.remove') }}
      </BaseButton>
    </template>
  </BaseModalSmall>
</template>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { flash } from '@/helpers/ui';
import type { AdyenBalanceAccount } from '@/types';
import { CLOSE_ADYEN_BALANCE_ACCOUNT } from '../graphql';

const props = defineProps<{
  balanceAccount: AdyenBalanceAccount;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

// Refetch by name: queries default to no-cache, and closing also switches off
// the wallet's share.
const { mutate: closeBalanceAccount, loading } = useMutation(
  CLOSE_ADYEN_BALANCE_ACCOUNT,
  {
    refetchQueries: ['getAdyenWallets'],
    awaitRefetchQueries: true,
  },
);

const errorMessage = ref('');

const submit = async () => {
  errorMessage.value = '';
  const response = await closeBalanceAccount({
    input: { id: props.balanceAccount.id },
  });
  const result = response?.data?.closeAdyenBalanceAccount;

  // Keep the modal open on an error so the owner sees why it was refused
  if (result?.errors?.[0]) {
    errorMessage.value = result.errors[0].message;
    return;
  }

  if (result?.adyenBalanceAccount) {
    flash(t('register.salonized_pay.dashboard.wallets.remove_modal.flash'));
  }

  emit('close');
};
</script>
